import { WaveformData } from "./types";

export const CURRENT_WAVEFORM_VERSION = 1;

/**
 * The entire streaming audio pipeline normalises all sources to 48 kHz
 * stereo interleaved PCM (L R L R …).  Mono inputs are upmixed to stereo
 * at the decode/record boundary so every downstream consumer — ring buffers,
 * mixer worklet, waveform calculation — can assume exactly 2 channels.
 */
const STEREO_CHANNELS = 2;
const STEP = 10000;

/**
 * Calculate waveform min/max data from either an `AudioBuffer` (legacy) or
 * a 48 kHz stereo interleaved `Float32Array` (streaming).
 */
export function calculateWaveform(source: AudioBuffer | Float32Array): WaveformData {
  if (source instanceof Float32Array) {
    return calculateWaveformFromPcm(source);
  }

  return calculateWaveformFromAudioBuffer(source);
}

/**
 * Waveform from 48 kHz stereo interleaved PCM (L R L R …).
 *
 * Indexes directly into the source buffer — no temporary channel copies needed.
 */
function calculateWaveformFromPcm(pcm: Float32Array): WaveformData {
  const frames = pcm.length / STEREO_CHANNELS;
  const width = Math.floor(frames / STEP);
  const data: number[][] = [];

  for (let i = 0; i < width; i++) {
    let min = 1.0;
    let max = -1.0;

    for (let j = 0; j < STEP; j++) {
      const offset = (i * STEP + j) * STEREO_CHANNELS;
      // Reduce stereo to mono by averaging L + R
      const datum = (pcm[offset] + pcm[offset + 1]) / STEREO_CHANNELS;

      if (datum < min) min = datum;
      if (datum > max) max = datum;
    }

    data.push([min, max]);
  }

  return { step: STEP, version: CURRENT_WAVEFORM_VERSION, data };
}

/**
 * Waveform from a Web Audio `AudioBuffer` (planar channel layout).
 *
 * Only used by the legacy (AudioBuffer-based) playback backend.
 * The streaming backend uses the interleaved PCM path above instead.
 *
 * Only reads one channel to limit memory usage — `copyFromChannel` allocates a
 * full-length `Float32Array` per channel copied.
 */
function calculateWaveformFromAudioBuffer(audioBuffer: AudioBuffer): WaveformData {
  // Hard-coded to one channel to limit memory usage.
  const channels = 1;
  const length = audioBuffer.length;
  const data: number[][] = [];

  const channelData: Float32Array<ArrayBuffer>[] = [];
  for (let i = 0; i < channels; i++) {
    channelData.push(new Float32Array(length));
    audioBuffer.copyFromChannel(channelData[i], i);
  }

  const width = Math.floor(length / STEP);

  for (let i = 0; i < width; i++) {
    let min = 1.0;
    let max = -1.0;

    for (let j = 0; j < STEP; j++) {
      // Reduce stereo to mono (currently single-channel, but formula generalises)
      const datum =
        channelData.reduce((acc, ch) => (acc += ch[i * STEP + j]), 0) /
        channelData.length;

      if (datum < min) min = datum;
      if (datum > max) max = datum;
    }

    data.push([min, max]);
  }

  return { step: STEP, version: CURRENT_WAVEFORM_VERSION, data };
}
